// Shared brand gate for /integrations and /v2/connections sections.
// Loading, error, and a no-brand empty state. Children render only
// for the active brand. A document from the previous id is not shown
// while a switch is in flight.

import type { ReactNode } from 'react';
import { HStack, Spinner, Text } from '@chakra-ui/react';
import { useBrand } from '../../brand/BrandContext';
import { statusErrorText } from './connectionStatus';
import { useBrandDetail } from './useBrandDetail';
import type { Brand } from './types';

export function BrandLoadState({ children }: { children: (brand: Brand) => ReactNode }) {
  const { activeBrand } = useBrand();
  const { brand, loading, error } = useBrandDetail();
  const currentId = activeBrand?.id || null;
  const stale = !!currentId && !!brand?._id && brand._id !== currentId;
  const waiting = loading || stale || (!!currentId && !brand && !error);

  if (waiting) {
    return (
      <HStack spacing={2} color="brand.muted" data-testid="brand-loading">
        <Spinner size="sm" />
        <Text fontSize="14px">Loading brand…</Text>
      </HStack>
    );
  }

  if (error) {
    return (
      <Text color="red.700" fontSize="13px" data-testid="brand-error">{statusErrorText(error, 'Failed to load brand')}</Text>
    );
  }

  if (!brand) {
    return (
      <Text fontSize="sm" color="brand.muted" data-testid="no-brand-empty">
        No brand is selected. Pick a brand to see connection status.
      </Text>
    );
  }

  return <>{children(brand)}</>;
}
